import { isSameDay, isBefore, formatISO } from 'date-fns';
import toDate from 'date-fns-tz/toDate';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

/**
 * #37133: whether the Details tab should default to editing the selected segment rather than adding a new
 * change. A segment starting after today (in the policy's timezone) is edited, as before. So is the term's
 * first segment on its own start day: a new change can't share its start date, so an endorsement of a term
 * effective today failed with "Segment already exists with this start date", and editing it is pinned to
 * that date (getStartMinMaxDate). A later segment starting today still defaults to a new change dated
 * tomorrow, because a user who can't backdate may not edit it to today.
 * `isFuture(new Date('yyyy-mm-dd'))` read the date as UTC midnight, the previous evening in every US
 * timezone, so it also flipped to "new change" the evening before a future segment started.
 */
export const defaultsToEditingSegment = (segment, policy) => {
  if (!segment?.startDate) {
    return false;
  }
  const startDate = String(segment.startDate).slice(0, 10);
  const today = getTodayStr(policy?.state);
  const isFirstSegment = policy?.segments?.[0]?.segmentId === segment.segmentId;
  return startDate > today || (startDate === today && isFirstSegment);
};

export const getDateViolations = ({ isEdit, policy, segment, startDate, minDate, toast }) => {
  const dateViolations = {
    BEFORE_MIN_DATE: isBefore(startDate, minDate) && !isSameDay(startDate, minDate),
    START_DATE_ALREADY_EXISTS:
      isEdit &&
      policy?.segments.find(
        (segmentFromList) =>
          segmentFromList.segmentId !== segment.segmentId &&
          isSameDay(toDate(segmentFromList.startDate), toDate(startDate))
      )
  };

  const foundViolation = Object.keys(dateViolations).find((violation) => dateViolations[violation]);

  if (foundViolation) {
    const dateViolationMessages = ({ startDateFormatted, minDateFormatted }) => {
      return {
        BEFORE_MIN_DATE: `Please choose a date later than or equal to ${minDateFormatted[1]}/${minDateFormatted[2]}/${minDateFormatted[0]}`,
        START_DATE_ALREADY_EXISTS: `Segment with start date ${startDateFormatted[1]}/${startDateFormatted[2]}/${startDateFormatted[0]} already exists.`
      };
    };

    toast.notify({
      type: 'error',
      message: dateViolationMessages({
        startDateFormatted: formatISO(startDate).split('T')[0].split('-'),
        endDateFormatted: policy.endDate.split('-'),
        minDateFormatted: formatISO(minDate).split('T')[0].split('-')
      })[foundViolation]
    });
    return true;
  }
  return false;
};

// Reactive, side-effect-free conflict check for the "Create New Change" path. Unlike
// getDateViolations (Edit path, toast on submit), this is called on every render so the
// date picker can show inline guidance the instant a taken date is selected (#31068).
// A new change creates a brand-new segment, so any existing segment on that day conflicts.
export const getNewChangeDateConflict = ({ policy, startDate }) => {
  if (!startDate || !policy?.segments) {
    return null;
  }

  const conflictingSegment = policy.segments.find((segment) => isSameDay(toDate(segment.startDate), toDate(startDate)));

  return conflictingSegment ? 'A change already exists for this date' : null;
};
